import {
  Component,
  Output,
  EventEmitter,
  HostListener,
  OnDestroy,
  inject
} from '@angular/core';
import { select, Store } from '@ngrx/store';
import * as reducers from '../../reducers';
import * as actions from '../../actions/operator-collection';
import {
  combineLatest,
  Observable,
  Subject,
} from 'rxjs';
import { Operator } from '../../models';
import { Lookup } from '../../../shared/models';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { MatDialogRef } from '@angular/material/dialog';

@Component({
  selector: 'operator-selection-dialog',
  templateUrl: './operator-selection-dialog.component.html',
  styleUrls: ['./operator-selection-dialog.component.scss']
})
export class OperatorSelectionDialogComponent implements OnDestroy {
  gridData$: Observable<GridDataResult>;
  loading$: Observable<boolean>;
  operators$: Observable<Array<Operator>>;
  lookup$: Observable<Lookup>;
  lookupChanged$: Subject<Lookup> = new Subject<Lookup>();
  @Output() operatorSelected = new EventEmitter<Operator>();
  @Output() close = new EventEmitter<void>();
  selectedOperator: Operator;
  private destroy$: Subject<void> = new Subject<void>();
  readonly dialogRef = inject(MatDialogRef<OperatorSelectionDialogComponent>);

  @HostListener('document:keydown.escape', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    this.closeDialog();
  }

  constructor(private store$: Store<any>) {
    this.operators$ = this.store$.pipe(
      select(reducers.getOperatorCollectionItems)
    );
    this.loading$ = this.store$.pipe(
      select(reducers.getOperatorCollectionItemsLoading)
    );
    this.lookup$ = this.store$.pipe(
      select(reducers.getOperatorCollectionLookup)
    );

    this.gridData$ = combineLatest([
      this.operators$,
      this.lookup$
    ])
      .pipe(
        takeUntil(this.destroy$),
        map(([operators, lookup]) => ({
          data: operators,
          total: lookup.total
        })));

    this.lookupChanged$
      .pipe(
        debounceTime(1000),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(lookup => {
        const lookupClone = { ...lookup, skip: 0 };
        this.onDataStateChange(lookupClone);
      });
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  onSelectionChange(operator: Operator) {
    this.selectedOperator = operator;
  }

  onOperatorSelectionConfirmed(): void {
    if (this.selectedOperator) {
      this.dialogRef.close(this.selectedOperator);
    }
  }

  onDataStateChange(lookup: Lookup): void {
    this.store$.dispatch(new actions.SetLookupAction(lookup));
    this.store$.dispatch(new actions.LoadAction());
    this.selectedOperator = null;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
